import {
  DropdownCountry1,
  createCountryStore,
} from '@/components/DropdownCountry'
import MyDatePicker, { useDatePicker } from '@/components/MyDatePicker'
import React, { useMemo } from 'react'
import useSWR from 'swr'
import DeviceDropdown, { createDeviceStore } from './DropdownDevice'
import SkuDropdown, { createSkuIdStore } from './DropdownSku'

import { createColumnVisibilityStore } from '@/components/zthd/data-table'
import { DataTableViewOptions } from '@/components/zthd/data-table-view-options'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useColumnFilters } from '@/components/data-table-selectors'
import {
  cancelColumn,
  dnuColumn,
  eventAtColumn,
  freeTryColumn,
  paidColumn,
  refundColumn,
  renewColumn,
} from './columns'
import { Button } from '@/components/ui/button'
import { blobFetcher } from '@/hooks'
import useSWRMutation from 'swr/mutation'

const CACHE_KEY = 'event1'

const useColumnVisibilityStore = createColumnVisibilityStore({}, CACHE_KEY)
const useCountry = createCountryStore('US', CACHE_KEY)
const useSkuId = createSkuIdStore('1634387596', CACHE_KEY)
const useDevice = createDeviceStore('total', CACHE_KEY)

const columns = [
  eventAtColumn,
  dnuColumn,
  freeTryColumn,
  paidColumn,
  refundColumn,
  renewColumn,
  cancelColumn,
  // billRetryColumn,
  // reactivateColumn,
  // gracePeriodColumn,
]

const Event1 = () => {
  const country = useCountry((state) => state.value)
  const updateCountry = useCountry((state) => state.update)
  const skuId = useSkuId((state) => state.value)
  const updateSkuId = useSkuId((state) => state.update)
  const { startDate, endDate } = useDatePicker((state) => ({
    startDate: state.startDate,
    endDate: state.endDate,
  }))

  const device = useDevice((state) => state.value)
  const updateDevice = useDevice((state) => state.update)
  const { columnVisibility, setColumnVisibility } = useColumnVisibilityStore()
  const { columnFilters } = useColumnFilters()

  const { data: events } = useSWR([
    '/apple/events',
    { pid: 12, country, startDate, endDate, skuId },
  ])
  const { data: dnus } = useSWR([
    '/apple/dnus',
    { pid: 12, country, startDate, endDate },
  ])
  const data = useMemo(() => {
    if (!events) return []
    const events1 = events.filter((e) => e[device] > 0)
    const uniqueAts = [...new Set(events1.map((item) => item.eventAt))]
    return uniqueAts.map((eventAt) => {
      const eventAtEvents = events1
        .filter((item) => item.eventAt === eventAt)
        .map(({ event, [device]: value }) => ({
          event,
          value,
        }))

      const freeTry =
        events1.find(
          (e) =>
            e.event === 'Start Introductory Offer' && e.eventAt === eventAt,
        )?.[device] || 0

      return {
        eventAt,
        events: eventAtEvents,
        freeTry,
        dnu: dnus?.find((item) => item.date === eventAt)?.[device],
      }
    })
  }, [events, dnus, device])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    enableMultiRowSelection: true,
    state: {
      columnFilters,
      columnVisibility,
    },
  })

  return (
    <>
      <div className="flex items-center gap-4 py-4 sm:gap-2 sm:py-2 flex-wrap">
        <DropdownCountry1 value={country} update={updateCountry} />
        <MyDatePicker getMaxDate="complete" />
        <SkuDropdown value={skuId} update={updateSkuId} />
        <DeviceDropdown value={device} update={updateDevice} />
        <DownloadButton />
        <DataTableViewOptions table={table} />
      </div>
      <DataTableWithBorder table={table} />
    </>
  )
}

const DownloadButton = () => {
  const startDate = useDatePicker((state) => state.startDate)
  const endDate = useDatePicker((state) => state.endDate)
  const skuId = useSkuId((state) => state.value)
  const country = useCountry((state) => state.value)

  const { trigger, isMutating } = useSWRMutation(
    ['/reports/apple', { startDate, endDate, pid: 12, country, skuId }],
    blobFetcher,
  )

  const handleDownloadCsv = async () => {
    const { blob, fileName } = await trigger()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName || 'apple_events2_report.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Button onClick={handleDownloadCsv} variant="outline">
      Download csv{isMutating && 'ing...'}
    </Button>
  )
}

export default Event1
